import { connect, getConnection, subscribe, unsubscribe } from '@/store';
import { HotspotState, HotspotMixinListener } from '@owc-types/Hotspot';
import { SeamlessConnection } from '@seamless/store';
import { Reducer } from 'redux';
import { AnyAction, DispatchFunction } from '../SeamlessStore';
export const HOTSPOT_NAMESPACE = 'OWC_HOTSPOT';
export const HotspotAction = {
  SET_HOTSPOT_ACTIVE: `${HOTSPOT_NAMESPACE}/SET_HOTSPOT_ACTIVE`,
  SHOW_HOTSPOT_TABS: `${HOTSPOT_NAMESPACE}/SHOW_HOTSPOT_TABS`,
  HIDE_HOTSPOT_TABS: `${HOTSPOT_NAMESPACE}/HIDE_HOTSPOT_TABS`,
};

export type HotspotDispatchers = {
  setActiveHotspot: (hotspotId: string, activeHotspotItemId: string) => (dispatch: DispatchFunction) => void;
  showTabsForHotspot: (hotspotId: string) => (dispatch: DispatchFunction) => void;
  hideTabsForHotspot: (hotspotId: string) => (dispatch: DispatchFunction) => void;
};

const initialState: HotspotState = {
  hiddenTabsHotspotIds: [],
  activeItemsMap: {},
};

const reducer: Reducer<HotspotState> = (
  state: HotspotState = initialState,
  action: AnyAction = { type: '' },
) => {
  const { type, payload = {} } = action;
  switch (type) {
    case HotspotAction.SET_HOTSPOT_ACTIVE:
      return {
        ...state,
        activeItemsMap: {
          ...state.activeItemsMap,
          [payload.hotspotId]: payload.activeHotspotItemId,
        },
      };
    case HotspotAction.SHOW_HOTSPOT_TABS:
      return {
        ...state,
        hiddenTabsHotspotIds: state.hiddenTabsHotspotIds.filter(hotspotId => hotspotId !== payload.hotspotId),
      };
    case HotspotAction.HIDE_HOTSPOT_TABS:
      return {
        ...state,
        hiddenTabsHotspotIds: state.hiddenTabsHotspotIds.includes(payload.hotspotId)
          ? state.hiddenTabsHotspotIds
          : [...state.hiddenTabsHotspotIds, payload.hotspotId],
      };
    default:
      return state;
  }
};

const setActiveHotspot = (hotspotId: string, activeHotspotItemId: string) => (dispatch: DispatchFunction) =>
  dispatch({
    type: HotspotAction.SET_HOTSPOT_ACTIVE,
    payload: { hotspotId, activeHotspotItemId },
  });

const showTabsForHotspot = (hotspotId: string) => (dispatch: DispatchFunction) =>
  dispatch({
    type: HotspotAction.SHOW_HOTSPOT_TABS,
    payload: { hotspotId },
  });

const hideTabsForHotspot = (hotspotId: string) => (dispatch: DispatchFunction) =>
  dispatch({
    type: HotspotAction.HIDE_HOTSPOT_TABS,
    payload: { hotspotId },
  });

export class HotspotConnection extends SeamlessConnection<HotspotDispatchers, HotspotState> {
  constructor() {
    super(HOTSPOT_NAMESPACE);
  }

  protected getReducer(): Reducer<HotspotState> {
    return reducer;
  }

  public getPublicDispatchers(): HotspotDispatchers {
    return {
      setActiveHotspot: (hotspotId: string, activeHotspotItemId: string) =>
        setActiveHotspot(hotspotId, activeHotspotItemId),
      showTabsForHotspot: (hotspotId: string) => showTabsForHotspot(hotspotId),
      hideTabsForHotspot: (hotspotId: string) => hideTabsForHotspot(hotspotId),
    };
  }

  protected get initialState(): HotspotState | undefined {
    return initialState;
  }
}

export const initializeHotspotState = async () => {
  connect(new HotspotConnection());
};

export const getHotspotStateConnection = async () => {
  await initializeHotspotState();
  return getConnection<HotspotDispatchers>(HOTSPOT_NAMESPACE);
};

export const subscribeHotspotState = async (id: string, listener: HotspotMixinListener) => {
  await getHotspotStateConnection();
  return subscribe(id, HOTSPOT_NAMESPACE, listener);
};
export const unsubscribeHotspotState = async (id: string, listener: HotspotMixinListener) => {
  await getHotspotStateConnection();
  return unsubscribe(id, HOTSPOT_NAMESPACE, listener);
};
